import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Pagination from "../Pagination/Pagination";
import FilterSection from "../../organisms/Sections/filter-controller-section/FilterSection";
import filterContentLeads from "../../../utils/helpers/helperDataset/lead-management-contents/filterContentLeads.json";
import UseAuthConfig from "../../../hooks/useAuthConfig";
import LoaderHome from "../../atoms/Loader-home/Loader-home";
import PerformanceStatusGuide from "../OperatorPerformanceGuide/OperatorPerformanceGuide";

type StatusColor = "green" | "orange" | "red";
type LabelClass = "excellent" | "top" | "good" | "below" | "critical";

interface OperatorPerformanceApiItem {
  id: number;
  profilePictureUrl: string;
  firstName: string;
  lastName: string;
  totalLeads: number;
  hotLeadsCount: number;
  warmLeadsCount: number;
  interestedLeadsCount: number;
  rejectedLeadsCount: number;
  hotWoarmLeadsPercentage: number;
  rejectedLeadsPercentage: number;
  performanceType: number;
}

interface OperatorPerformanceApiResponse {
  pageNumber: number;
  pageSize: number;
  firstPage: string;
  lastPage: string;
  totalPages: number;
  totalRecords: number;
  nextPage: string | null;
  previousPage: string | null;
  data: OperatorPerformanceApiItem[];
  succeeded: boolean;
}

interface Operator {
  id: number;
  profilePictureUrl: string;
  initials: string;
  name: string;
  status: string;
  statusColor: StatusColor;
  total: number;
  hot: number;
  warm: number;
  interested: number;
  rejected: number;
  percent: number;
  rejectedLeadsPercentage: number;
  label: string;
  labelClass: LabelClass;
  featured: boolean;
}

interface OperatorPerformanceProps {
  pageSize?: number;
  isShowFilter?: boolean;
  isShowRejectionRate?: boolean;
  isShowView?: boolean;
  isShowViewAll?: boolean;
  isHotWarmPercentage?: boolean;
  isHotCounter?: boolean;
  isWarmCounter?: boolean;
  isInterestedCounter?: boolean;
  isRejectedCounter?: boolean;
  isShowGuide?: boolean;
}

interface OperatorRowProps {
  operator: Operator;
  isShowFilter?: boolean;
  isShowRejectionRate?: boolean;
  isShowView?: boolean;
  isHotWarmPercentage?: boolean;
  isHotCounter?: boolean;
  isWarmCounter?: boolean;
  isInterestedCounter?: boolean;
  isRejectedCounter?: boolean;
}

const getInitials = (firstName: string, lastName: string) => {
  const first = firstName?.trim()?.charAt(0) || "";
  const last = lastName?.trim()?.charAt(0) || "";
  return `${first}${last}`.toUpperCase();
};

const getPerformanceMeta = (
  performanceType: number
): { label: string; labelClass: LabelClass; featured: boolean } => {
  switch (performanceType) {
    case 1:
      return {
        label: "Top Performer",
        labelClass: "top",
        featured: true,
      };
    case 2:
      return {
        label: "On Track",
        labelClass: "good",
        featured: true,
      };
    case 3:
      return {
        label: "Needs Attention",
        labelClass: "below",
        featured: false,
      };
    default:
      return {
        label: "Needs Attention",
        labelClass: "below",
        featured: false,
      };
  }
};

const mapApiOperatorToUiOperator = (
  item: OperatorPerformanceApiItem
): Operator => {
  const performance = getPerformanceMeta(item.performanceType);

  return {
    id: item.id,
    profilePictureUrl: item.profilePictureUrl,
    initials: getInitials(item.firstName, item.lastName),
    name: `${item.firstName} ${item.lastName}`.trim(),
    status: "Online",
    statusColor: "green",
    total: item.totalLeads,
    hot: item.hotLeadsCount,
    warm: item.warmLeadsCount,
    interested: item.interestedLeadsCount,
    rejected: item.rejectedLeadsCount,
    percent: item.hotWoarmLeadsPercentage,
    rejectedLeadsPercentage: item.rejectedLeadsPercentage,
    label: performance.label,
    labelClass: performance.labelClass,
    featured: performance.featured,
  };
};

const OperatorRow: React.FC<OperatorRowProps> = ({
  operator,
  isShowRejectionRate,
  isShowView,
  isHotWarmPercentage,
  isHotCounter,
  isWarmCounter,
  isInterestedCounter,
  isRejectedCounter,
}) => {
  const navigate = useNavigate();

  return (
    <div className="operator-row">
      <div className="operator-top">
        <div className="operator-user">
          <div className="avatar">{operator.initials}</div>

          <div className="operator-info">
            <div className="name-line">
              <h3>{operator.name}</h3>
              {operator.featured && <span className="star">★</span>}
            </div>
          </div>
        </div>

        <div
          className={`performance-badge mobile-badge mobile-badge-page chip chip--status-${operator.labelClass}`}
        >
          {operator.label}
        </div>
      </div>

      <div className="operator-bottom">
        <div className="metrics">
          <div className="metric-item">
            <span className="metric-label">Total</span>
            <span className="metric-value">{operator.total}</span>
          </div>

          <div
            className={`performance-badge mobile-badge mobile-badge-summery chip chip--status-${operator.labelClass}`}
          >
            {operator.label}
          </div>

          {isHotCounter && (
            <div className="metric-item">
              <span className="metric-label">High Intent</span>
              <span className="metric-value hotLead">{operator.hot}</span>
            </div>
          )}

          {isWarmCounter && (
            <div className="metric-item">
              <span className="metric-label">Medium Intent</span>
              <span className="metric-value warmLead">{operator.warm}</span>
            </div>
          )}

          {isInterestedCounter && (
            <div className="metric-item">
              <span className="metric-label">Low Intent</span>
              <span className="metric-value">{operator.interested}</span>
            </div>
          )}

          {isRejectedCounter && (
            <div className="metric-item desktop-only">
              <span className="metric-label">Rejected</span>
              <span className="metric-value rejected">{operator.rejected}</span>
            </div>
          )}
        </div>

        <div className="right-side">
          {isHotWarmPercentage && (
            <div className="score-badge">
              <span className="score-title">High Intent Rate :</span>
              <span className="score-value"> {operator.percent} %</span>
            </div>
          )}

          {isShowRejectionRate && (
            <div className="metric-item desktop-only">
              <span className="metric-label">Rejected Rate</span>
              <span className="metric-value-rejected">
                {operator.rejectedLeadsPercentage}%
              </span>
            </div>
          )}

          <div
            className={`performance-badge desktop-badge chip chip--soft chip--status-${operator.labelClass}`}
          >
            {operator.label}
          </div>

          {isShowView && (
            <button
              type="button"
              onClick={() => navigate(`/operators/${operator.id}`)}
              className="view-btn"
            >
              View <span>›</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const OperatorPerformance: React.FC<OperatorPerformanceProps> = ({
  pageSize = 5,
  isShowFilter = false,
  isShowRejectionRate = false,
  isShowView = false,
  isShowViewAll = false,
  isHotWarmPercentage = false,
  isHotCounter = true,
  isWarmCounter = true,
  isInterestedCounter = true,
  isRejectedCounter = true,
  isShowGuide = false,
}) => {
  const { protectedResources }: any = UseAuthConfig();
  const [operators, setOperators] = useState<Operator[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const accessToken = localStorage.getItem("AccessToken");

  const [operatorRequests, setOperatorRequests] = useState({
    PageNumber: 1,
    SearchText: "",
    SortOrder: 1,
    PageSize: pageSize,
    OrderBy: "",
    ParameterOne: "",
    ParameterTwo: "",
    ParameterThree: "",
    url: protectedResources.apiTodoList.endpoint,
    accessToken: accessToken || "",
  });

  useEffect(() => {
    const fetchOperators = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await fetch(
          `${process.env.REACT_APP_API_TODO_LIST_ENDPOINT}/leads/operatorPerformance?pageNumber=${pageNumber}&pageSize=${pageSize}&searchText=${operatorRequests.SearchText}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              ...(accessToken
                ? { Authorization: `Bearer ${accessToken}` }
                : {}),
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const result: OperatorPerformanceApiResponse = await response.json();

        if (!result.succeeded) {
          throw new Error("API response was not successful.");
        }

        setOperators(result.data.map(mapApiOperatorToUiOperator));
        setTotalPages(result.totalPages);
        setTotalRecords(result.totalRecords);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Something went wrong.";
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchOperators();
  }, [pageNumber, pageSize, operatorRequests.SearchText, accessToken]);

  const handleSetFilteredValue = (inputValue: string) => {
    setPageNumber(1);
    setOperatorRequests((prevState) => ({
      ...prevState,
      SearchText: inputValue || "",
    }));
  };

  return (
    <div className="operator-widget">
      {isShowFilter && (
        <div className="widget-header">
          <FilterSection
            {...filterContentLeads}
            handleInputText={handleSetFilteredValue}
          />
        </div>
      )}

      {isShowGuide && <PerformanceStatusGuide />}

      {isShowViewAll && (
        <Link to="/operators" className="view-all-operators">
          View All
        </Link>
      )}

      {loading && (
        <div>
          <LoaderHome />
          <LoaderHome />
          <LoaderHome />
        </div>
      )}

      {error && <p>{error}</p>}

      {!loading && !error && (
        <div className="operator-list">
          {operators.map((operator) => (
            <OperatorRow
              key={operator.id}
              operator={operator}
              isShowRejectionRate={isShowRejectionRate}
              isShowView={isShowView}
              isHotWarmPercentage={isHotWarmPercentage}
              isHotCounter={isHotCounter}
              isWarmCounter={isWarmCounter}
              isInterestedCounter={isInterestedCounter}
              isRejectedCounter={isRejectedCounter}
            />
          ))}

          <Pagination
            pageNumber={pageNumber}
            onPageChange={(page) => setPageNumber(page)}
            totalPages={totalPages}
            pageSize={pageSize}
            totalRecords={totalRecords}
            label="Operators"
            pageCount={totalRecords}
          />
        </div>
      )}
    </div>
  );
};

export default OperatorPerformance;
